import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface OpeningBalanceItem {
  id?: string;
  line_number: number;
  account_code: string;
  subconto_1_type?: string | null;
  subconto_1_id?: string | null;
  subconto_2_type?: string | null;
  subconto_2_id?: string | null;
  subconto_3_type?: string | null;
  subconto_3_id?: string | null;
  debit_amount: string;
  credit_amount: string;
  warehouse_id?: string | null;
  quantity?: string | null;
  comment?: string | null;
}

export interface OpeningBalanceListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  total_debit: string;
  total_credit: string;
  posted: boolean;
  deletion_mark: boolean;
  // Block 5.3c:
  state?: CancellableState;
}

export interface OpeningBalance extends OpeningBalanceListItem {
  posted_at: string | null;
  comment: string | null;
  items: OpeningBalanceItem[];
  // Block 5.3c:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface OpeningBalanceCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  comment?: string | null;
  items: OpeningBalanceItem[];
}

export type OpeningBalanceUpdate = Partial<OpeningBalanceCreate>;

export const openingBalances = {
  list: (params?: { organization_id?: string; posted?: boolean; limit?: number; offset?: number }) =>
    api.get<Page<OpeningBalanceListItem>>('/opening-balances', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<OpeningBalance>(`/opening-balances/${id}`).then((r) => r.data),
  create: (payload: OpeningBalanceCreate) =>
    api.post<OpeningBalance>('/opening-balances', payload).then((r) => r.data),
  update: (id: string, payload: OpeningBalanceUpdate) =>
    api.patch<OpeningBalance>(`/opening-balances/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/opening-balances/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<OpeningBalance>(`/opening-balances/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<OpeningBalance>(`/opening-balances/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/opening-balances/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

// =================== Import API (Порція Б.2-Б.4 контракт) ===================
//
// Контракт нового імпорту (узгоджено backend Б.2-Б.4):
//
// 1. `target_organization_id` — обов'язковий QUERY-параметр (НЕ у тілі).
//    Backend перевіряє його ПЕРШОЮ дією (scope.assert_access). Чужий →
//    403 миттєво, rows навіть не парсяться.
//
// 2. `organization_id` per-row — ЗАБОРОНЕНО у схемі. Pydantic
//    extra='forbid' відкине 422. Не клади у row-тип.
//
// 3. `tenant_id` — взагалі не приймається від клієнта. Backend бере з
//    X-Tenant-Id заголовка.
//
// 4. `profession_classifier` — імпорт заборонено повністю (глобальний
//    довідник, Б.4). Тут НЕМАЄ функції.
//
// 5. `employees` — ч.1084: фізособа + співробітник + стартовий кадровий
//    стан (оклад). Посада/підрозділ — текстом; повтор файлу без дублів.

export interface ImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  errors: string[];
  /** Не-блокуючі попередження (створені посади, карантин тощо). */
  warnings?: string[];
  quarantined?: number;
}

export interface ContractorImportRow {
  external_id?: string | null;
  code?: string | null;
  name: string;
  full_name?: string | null;
  contractor_type?: 'LEGAL' | 'INDIVIDUAL_FOP' | 'INDIVIDUAL' | 'SEPARATE_DIVISION';
  edrpou?: string | null;
  inn?: string | null;
  is_vat_payer?: boolean;
  vat_number?: string | null;
  is_buyer?: boolean;
  is_supplier?: boolean;
  is_other?: boolean;
}

export interface NomenclatureImportRow {
  external_id?: string | null;
  code?: string | null;
  article?: string | null;
  name: string;
  full_name?: string | null;
  item_type?: 'GOOD' | 'SERVICE';
}

// УВАГА: organization_id у row тепер ЗАБОРОНЕНО (Б.2 extra='forbid').
// owner — через target_organization_id у query.
export interface DepartmentImportRow {
  external_id?: string | null;
  code: string;
  name: string;
  parent_external_id?: string | null;
}

export interface PositionImportRow {
  external_id?: string | null;
  code: string;
  name: string;
  kp_code?: string | null;
}

export interface EmployeeImportRow {
  external_id?: string | null;
  code?: string | null;
  last_name: string;
  first_name: string;
  middle_name?: string | null;
  inn?: string | null;
  /** Посада — назва або код; немає такої — створюється. */
  position?: string | null;
  /** Підрозділ — назва або код; немає такого — створюється. */
  department?: string | null;
  /** ДД.ММ.РРРР або РРРР-ММ-ДД; обовʼязкова для штатних. */
  hire_date?: string | null;
  /** Оклад, грн. */
  salary?: string | null;
  /** «штатний» (за замовчуванням) / «сумісник» / «ЦПХ». */
  employment_type?: string | null;
}

// Сутності, для яких треба обирати організацію (owner = organization_id).
// Для contractors/nomenclature/positions owner = tenant_id, селектор
// не показується. Цей список дзеркалить backend модель ізоляції
// (Опція B cascade, Б.1) і ТЕСТУЄТЬСЯ grep-guard'ом у Б.5.
export const NEEDS_ORG_SELECTOR = ['departments', 'employees'] as const;
export type ImportKind =
  | 'contractors'
  | 'nomenclature'
  | 'departments'
  | 'positions'
  | 'employees';

// Сутність profession_classifier СВІДОМО ВИДАЛЕНА (Б.4):
// ref.profession_classifier — глобальний реєстр КП, наповнюється
// централізовано. Tenant-write заборонено. Backend повертає 422 завжди.
// Якщо клієнт надішле — це баг фронту, не helper'а.

export const imports = {
  contractors: (targetOrganizationId: string, source: string, rows: ContractorImportRow[]) =>
    api
      .post<ImportResult>(
        '/import/contractors',
        { source, rows },
        { params: { target_organization_id: targetOrganizationId } },
      )
      .then((r) => r.data),
  nomenclature: (targetOrganizationId: string, source: string, rows: NomenclatureImportRow[]) =>
    api
      .post<ImportResult>(
        '/import/nomenclature',
        { source, rows },
        { params: { target_organization_id: targetOrganizationId } },
      )
      .then((r) => r.data),
  departments: (targetOrganizationId: string, source: string, rows: DepartmentImportRow[]) =>
    api
      .post<ImportResult>(
        '/import/departments',
        { source, rows },
        { params: { target_organization_id: targetOrganizationId } },
      )
      .then((r) => r.data),
  positions: (targetOrganizationId: string, source: string, rows: PositionImportRow[]) =>
    api
      .post<ImportResult>(
        '/import/positions',
        { source, rows },
        { params: { target_organization_id: targetOrganizationId } },
      )
      .then((r) => r.data),
  employees: (targetOrganizationId: string, source: string, rows: EmployeeImportRow[]) =>
    api
      .post<ImportResult>(
        '/import/employees',
        { source, rows },
        { params: { target_organization_id: targetOrganizationId } },
      )
      .then((r) => r.data),
};

/** ч.1084: Excel (.xlsx) → шапка + рядки першого аркуша (нічого не пише). */
export interface ParsedImportFile {
  headers: string[];
  rows: string[][];
  sheet?: string | null;
}

export const parseImportFile = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post<ParsedImportFile>('/import/parse-file', form)
    .then((r) => r.data);
};
